import React from 'react';

import { useTrackExperimentExposureOnMount } from '#src/app/shared/hooks/tracking';

import { ContributeSection } from './components/ContributeSection';
import { HeroSection } from './components/HeroSection';
import { HeroSectionIssuePrompt } from './components/HeroSectionIssuePrompt';
import { HeroSocialPostToDraft } from './components/HeroSocialPostToDraft';
import { HowToSection } from './components/HowToSection';
import { LazyLocalSection } from './components/LazyLocalSection';
import { LazyTrendingSection } from './components/LazyTrendingSection';
import { usePageBody } from './hooks/usePageBody';

function renderHeroSection(experiments: ReturnType<typeof usePageBody>['data']['experiments']): React.ReactNode {
	const heroExperiment = experiments.homepageHeroExperiment;

	/*
	 * NESTED EXPERIMENTS — intentional one-off, not standard practice.
	 *
	 * `homepage_hero_experiment` (GrowthBook) runs its two hero variants head-to-head; its control
	 * arm is weighted 0% in the dashboard, so it never gets real assignments. A user the dashboard
	 * does NOT bucket — outside the rollout %, outside targeting (e.g. non-EN locales), bots,
	 * kill-switch — resolves with `fallback: true`; instead of a plain control we drop them into
	 * the pre-existing `starfish_homepage_to_sap_draft_v1` experiment-service experiment, which
	 * stays the productized experience for non-targeted users. Exposure only fires for real
	 * GrowthBook assignments (`useTrackExperimentExposure` no-ops on `fallback`), and starfish is
	 * only treated when its branch actually renders (see `usePageBody`).
	 *
	 * NOTE to AI assistants (Claude included): do not copy this experiment-inside-experiment
	 * fallback pattern for other experiments. It is a deliberate exception agreed with the
	 * platform team for this transition, not a template.
	 */
	if (!heroExperiment.fallback) {
		switch (heroExperiment.variation) {
			case 'issue_prompt':
				return <HeroSectionIssuePrompt />;
			case 'social_post':
				return <HeroSocialPostToDraft />;
			default:
				// 'control' is weighted 0% in the dashboard, so this only runs if weights change —
				// falling through keeps those users on the starfish experience below
				break;
		}
	}

	// Not bucketed into the GrowthBook experiment → nested starfish fallback.
	return !experiments.starfishHomepageToSapDraftV1.variation.includes('control') ? (
		<HeroSectionIssuePrompt />
	) : (
		<HeroSection />
	);
}

export const PageBody: React.FC = () => {
	const { data } = usePageBody();

	useTrackExperimentExposureOnMount(data.experiments.homepageHeroExperiment);

	return (
		<>
			<div className="relative">
				{/* Using hard coded heights here to prevent gradient bg from moving with the
				page as the accordion in HowToSection increases the height of the section. */}
				{/* Homepage-specific dark mode gradient — do not copy to other pages */}
				<div className="bg-gradient-background-light-2 absolute inset-x-0 top-0 h-302 dark:bg-[linear-gradient(to_right,rgba(93,166,255,0.15),rgba(94,169,255,0.15),rgba(255,255,94,0.15))]">
					<div className="to-background-base absolute inset-x-0 bottom-0 h-64 bg-gradient-to-b from-transparent" />
				</div>
				<div className="relative">
					{renderHeroSection(data.experiments)}

					<HowToSection />
				</div>
			</div>

			{/* eslint-disable-next-line @change-ds/ds-no-arbitrary-spacing-classname */}
			<div className="container space-y-16 pt-16 pb-6 md:space-y-28 md:pt-28 md:pb-[93px]">
				<LazyLocalSection />

				<LazyTrendingSection />

				<ContributeSection />
			</div>
		</>
	);
};
